<template>
  <page>
    <cv-link to="/">← Back</cv-link>
    <h1>More about this research</h1>
    <!-- <cv-link to="/">see it on arXiv.org</cv-link> -->
    <section>
      <p>Recently, AI models based on deep generative neural networks have shown promise for automating discovery of molecules. However, several challenges remain for applying existing generative AI models to accelerate the design of novel drug candidates.</p>
      <p>Discovery tasks frequently involve understanding complex relationships and exploring a vast repertoire of possible configurations, while meeting multiple constraints and design objectives. For example, optimal drug candidate molecules should have high target affinity, as well as high enough off-target selectivity, drug-likeliness and synthetic accessibility.</p>
      <p>Another issue is limited exploratory and expansion capability of existing generative frameworks, as they limit learning to molecules that bind to a specific target class present in the training data and therefore need expensive model retraining to expand/explore beyond the limited training data.</p>
      <p>
        <b>IBM Research</b> has been actively developing robust AI generative frameworks, which can handle above-mentioned challenges and enable generation of optimal meta-materials, antibiotics, small-molecule drugs, etc. A particular area of focus has been the development of efficient algorithms that can impose multiple controls on the generation process to yield desired outcome. Given the urgency of the ongoing coronavirus pandemic, we have applied our methodology to generate novel small molecules for novel COVID-19 targets that could be potential COVID-19 drug candidates.
      </p>
      <p>We have generated novel small-molecule drug candidates that bind to one of three relevant SARS-CoV-2 target proteins:</p>
      <ol>
        <li>SARS-CoV-2 Main Protease</li>
        <li>SARS-COV-2 NSP9 Replicase</li>
        <li>Receptor-Binding Domain (RBD) of SARS-COV-2 S protein</li>
      </ol>
      <p>
        Details of this work can be found at
        <cv-link href="https://arxiv.org/abs/2004.01215" target="_blank">arxiv.org/abs/2004.01215</cv-link>.
      </p>
      <p>
        <img src="../assets/gen_pipeline.png" />
        <br />
      </p>
      <figcaption>Pipeline of generative modeling and sampling.</figcaption>
      <p>At the high level, our generative modeling framework consists of the following steps:</p>
      <ol>
        <li>
          Learn the latent space of small molecules using a Variational Autoencoder model trained on the
          <cv-link href="https://github.com/molecularsets/moses" target="_blank">ZINC MOSES</cv-link>
          {{ }}and{{ }}
          <cv-link href="http://www.bindingdb.org/bind/index.jsp" target="_blank">BindingDB</cv-link>
          {{ }}datasets.
        </li>
        <li>Train regression models using the latent features of the small molecules (and protein sequence embeddings) to predict attributes of interest, i.e. target binding affinity, off-target selectivity, and drug-likeliness. Binding affinity data from BindingDB were used for training an affinity predictor.</li>
        <li>Given a specific target sequence that may or may not be present in the training data, use a controlled sampling scheme to generate molecules from the latent space with high target binding affinity, high selectivity, and high drug-likeliness.</li>
      </ol>
      <p>Given the urgency of the COVID-19 situation, we are exposing a first set of generated molecules via an interactive visual interface, to allow researchers to explore these novel molecules, understand their characteristics and relationship to COVID-19, and identify candidates that might have desirable properties to be further pursued in drug development. We are working with several partners on validating the AI-generated molecules by using in silico simulations and wet lab experiments, and will include those validation results into the exploration tool as they arrive. We will also submit this work to a peer-reviewed venue soon.</p>
      <p>
        If you are interested in adding or exploring other molecular features, or target proteins, or would like to work with us on further validating the generated molecules, you can contact us
        <cv-link href="mailto:covidmol@us.ibm.com">via email</cv-link> or join the cogmol <cv-link href="https://join.slack.com/t/cogmol/shared_invite/zt-e6dftawi-6v8A6bET7Kmn~Y0FKvqAxg">slack community</cv-link> to make suggestions and contributions, and to ask questions.
      </p>
      <p>The molecules should be referenced as in:</p>

      <pre style="width:100%; overflow: auto;" ><code>@misc{chenthamarakshan2020targetspecific,
    title={Target-Specific and Selective Drug Design for COVID-19 Using Deep Generative Models},
    author={Vijil Chenthamarakshan and Payel Das and Inkit Padhi and Hendrik Strobelt and Kar Wai Lim and Ben Hoover and Samuel C. Hoffman and Aleksandra Mojsilovic},
    year={2020},
    eprint={2004.01215},
    archivePrefix={arXiv},
    primaryClass={cs.LG}
}
</code></pre>
      <div class="separator"></div>
      <footer>
        <p>Additional technical details can be found in the following publications from our team.</p>
        <ol>
          <li>
            Vijil Chenthamarakshan, Payel Das, Inkit Padhi, Hendrik Strobelt,Kar Wai Lim, Ben Hoover, and Aleksandra Mojsilovic. Target-Specific Drug Design for COVID-19 Using Deep Generative Models. (Technical Report, Arxive 2020).
            <cv-link target="_blank" href="https://arxiv.org/abs/2004.01215">arxiv.org/abs/2004.01215</cv-link>.
          </li>
          <li>
            Tom Sercu, Sebastian Gehrmann, Hendrik Strobelt, Payel Das, Inkit Padhi, Cicero Dos Santos, Kahini Wadhawan, Vijil Chenthamarakshan. Interactive Visual Exploration of Latent Space (IVELS) for Peptide Auto-Encoder Model Selection. ICLR DGS workshop(2019).
            <cv-link
              target="_blank"
              href="https://openreview.net/pdf?id=Hkl8EILFdN"
            >openreview.net/pdf?id=Hkl8EILFdN</cv-link>.
          </li>
          <li>Vijil Chenthamarakshan, Payel Das, Tom Sercu, Inkit Padhi.Generative Models For Target-specific Drug Design. NeurIPS LMRL workshop(2019).</li>
          <li>
            Payel Das, Kahini Wadhawan, Oscar Chang, Tom Sercu, Cicero dos Santos, Matthew Riemer, Vijil Chenthamarakshan, Inkit Padhi, Aleksandra Mojsilovic. Pepcvae: Semi-supervised targeted design of antimicrobial peptide sequences. NeurIPS ML4MM workshop(2018).
            <cv-link
              target="_blank"
              href="https://arxiv.org/pdf/1810.07743.pdf"
            >arxiv.org/pdf/1810.07743.pdf</cv-link>.
          </li>
          <li>
            Zhao Qin, Lingfei Wu, Hui Sun, Siyu Huo, Tengfei Ma, Eugene Lim, Pin-Yu Chen, Benedetto Marelli, Markus J. Buehler. Artificial intelligence method to design and fold alpha-helical structural proteins from the primary amino acid sequence.Extreme Mechanics Letters 2020.
            <cv-link
              target="_blank"
              href="https://www.sciencedirect.com/science/article/pii/S2352431620300274"
            >sciencedirect.com/science/article/pii/S2352431620300274</cv-link>.
          </li>
          <li>Thanh Nguyen, Youssef Mroueh, Samuel Hoffman, Payel Das, Pierre Dognin, Giuseppe Romano, and Chinmay Hegde.Nano-Material Configuration Design with Deep Surrogate Langevin Dynamics. ICLR DeepDiffEq Workshop 2020</li>
          <li>Kajino, Hiroshi. Molecular Hypergraph Grammar with Its Application to Molecular Optimization. ICML 2019.</li>
          <li>
            Ma, T., Xiao, C., Zhou, J., & Wang, F. (2018). Drug similarity integration through attentive multi-view graph auto-encoders. IJCAI 2018.
            <cv-link
              target="_blank"
              href="https://arxiv.org/abs/1804.10850"
            >arxiv.org/abs/1804.10850</cv-link>.
          </li>
          <li>
            Ma, Tengfei, Jie Chen, and Cao Xiao. "Constrained generation of semantically valid graphs via regularizing variational autoencoders." NeurIPS 2018.
            <cv-link
              target="_blank"
              href="http://papers.nips.cc/paper/7942-constrained-generation-of-semantically-valid-graphs-via-regularizing-variational-autoencoders"
            >papers.nips.cc/paper/7942-constrained-generation-of-semantically-valid-graphs-via-regularizing-variational-autoencoders</cv-link>.
          </li>
          <li>
            Ma, T., Shang, J., Xiao, C., & Sun, J. GENN: Predicting Correlated Drug-drug Interactions with Graph Energy Neural Networks. Submitted to KDD 2020.
            <cv-link
              target="_blank"
              href="https://arxiv.org/abs/1910.02107"
            >arxiv.org/abs/1910.02107</cv-link>.
          </li>
          <li>
            Mroueh, Y., Sercu, T., Rigotti, M., Padhi, I., & dos Santos, C. N Sobolev Independence Criterion. NeurIPS 2019.
            <cv-link
              target="_blank"
              href="https://arxiv.org/pdf/1910.14212.pdf"
            >arxiv.org/pdf/1910.14212.pdf</cv-link>.
          </li>
          <li>
             J Born, M Manica, A Oskooei, J Cadow, K Borgwardt, M Rodríguez Martínez. PaccMann-RL : Designing anticancer drugs from transcriptomic data via reinforcement learning. Neurips ML4physicalsciences Workshop (2019).
            <cv-link
              target="_blank"
              href="https://arxiv.org/abs/1909.05114"
            >https://arxiv.org/abs/1909.05114</cv-link>.
          </li>
        </ol>
      </footer>
    </section>
  </page>
</template>
<script>
import Page from "@/components/Page.vue";
export default {
  name: "Research",
  components: { Page }
};
</script>
